import axios from "axios";
import { RequestedTeam } from "../entities/RequestedTeam";
import { JgTeam } from "../entities/JgTeam";
import { ManualTeam } from "../entities/ManualTeam";
import { useErrorStore } from "../errorStore";

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const authToken = localStorage.getItem("authToken");
const headers = {
  Authorization: `Bearer ${authToken}`,
};
const config = { headers };

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("authToken");
      localStorage.setItem("redirectUrl", window.location.pathname);
      window.location.href = apiBaseUrl + "/api/login";
    }
    return Promise.reject(error);
  }
);

export const getRemoteCurrentUser = async () => {
  try {
    const request = await axios.get(apiBaseUrl + "/api/me", config);
    return request.data;
  } catch (error: any) {
    return { ...error.response?.data, status: error.response?.status };
  }
};
export const getRemoteCoursesForCurrentUser = async () => {
  try {
    const request = await axios.get(apiBaseUrl + "/api/me/courses", config);
    return request.data;
  } catch (error: any) {
    return error.response.data;
  }
};
export const getRemoteTeachersForCurrentUserCourses = async () => {
  try {
    const request = await axios.get(
      apiBaseUrl + "/api/me/courses/teachers",
      config
    );
    return request.data;
  } catch (error: any) {
    return error.response.data;
  }
};
export const getRemoteManualCoursesForCurrentUser = async () => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/me/manual-courses",
    config
  );
  return data;
};
export const getRemoteOldCourseTemplatesForCurrentUser = async (
  teachers: any = []
) => {
  const teachersString = JSON.stringify(teachers);
  const { data } = await axios.get(
    apiBaseUrl + "/api/me/old-courses?teachers=" + teachersString,
    config
  );
  return data;
};

export const getRemoteTeachers = async () => {
  const { data } = await axios.get(apiBaseUrl + "/api/teachers", config);
  return data;
};
export const getRemoteSemesters = async () => {
  const { data } = await axios.get(apiBaseUrl + "/api/semesters", config);
  return data;
};
export const getRemoteStgs = async () => {
  const { data } = await axios.get(apiBaseUrl + "/api/stgs", config);
  return data;
};
export const getRemoteDepartments = async () => {
  const { data } = await axios.get(apiBaseUrl + "/api/departments", config);
  return data;
};
export const getRemoteFachbereiche = async () => {
  const { data } = await axios.get(apiBaseUrl + "/api/fachbereiche", config);
  return data;
};
export const getRemoteCategories = async () => {
  const { data } = await axios.get(apiBaseUrl + "/api/categories", config);
  return data;
};
export const getRemoteActiveRequestedCourses = async () => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/all-requested-courses",
    config
  );
  return data;
};
export const getRemoteCompletedRequestedCourses = async () => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/all-requested-courses?isCompleted=1",
    config
  );
  return data;
};
export const getRemoteManualCourseByCourseId = async (
  courseId: string | undefined
) => {
  if (courseId) {
    try {
      const request = await axios.get(
        apiBaseUrl + "/api/manual-courses/" + courseId,
        config
      );
      return request.data;
    } catch (error: any) {
      return error.response.data;
    }
  }
};
export const getRemoteCourseByCourseId = async (
  courseId: string | undefined
) => {
  if (courseId) {
    try {
      const request = await axios.get(
        apiBaseUrl + "/api/courses/" + courseId,
        config
      );
      return request.data;
    } catch (error: any) {
      return error.response.data;
    }
  }
};

export const createRemoteCourse = async (formData: FormData) => {
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/courses",
    data: formData,
    headers,
  });
};
export const requestRemoteCourse = async (formData: FormData) => {
  try {
    await axios({
      method: "post",
      url: apiBaseUrl + "/api/manual-courses",
      data: formData,
      headers,
    });
  } catch (error: any) {
    return error.response.data;
  }
};
export const createRemoteManualMoodleCourse = async (formData: FormData) => {
  try {
    await axios({
      method: "post",
      url: apiBaseUrl + "/api/manual-moodle-courses",
      data: formData,
      headers,
    });
  } catch (error: any) {
    return error.response.data;
  }
};
export const changeRemoteUser = async (formData: FormData) => {
  try {
    const { data } = await axios({
      method: "post",
      url: apiBaseUrl + "/api/change-user",
      data: formData,
      headers,
    });
    return data;
  } catch (error: any) {
    console.error(error.response.data);
  }
};

export const createRemoteTeams = async (formData: FormData) => {
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/teams",
    data: formData,
    headers,
  });
};

export const joinTeamAsOwner = async (teamId: string) => {
  const { data } = await axios({
    method: "post",
    url: apiBaseUrl + "/api/teams/" + teamId + "/join",
    headers,
  });
  return data;
};

export const resolveTemplateSyncError = async (formData: FormData) => {
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/resolve-template-sync-error",
    data: formData,
    headers,
  });
};

export const rejectRemoteRequestedCourse = async (formData: FormData) => {
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/reject-requested-course",
    data: formData,
    headers,
  });
};

export const getRemoteGlobalInfoText = async () => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/settings/global_info_message",
    config
  );
  return data;
};
export const setRemoteGlobalInfoText = async (formData: FormData) => {
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/settings/global_info_message",
    data: formData,
    headers,
  });
};

export type FilterType = {
  teamType: string;
  status: string;
  userName: string;
};
export const getAllRequestedTeams = async (filter: FilterType) => {
  const { data } = await axios.get(
    apiBaseUrl +
      "/api/request-teams" +
      "?userName=" +
      filter.userName +
      "&teamType=" +
      filter.teamType +
      "&status=" +
      filter.status,
    config
  );
  return data as RequestedTeam[];
};
export const requestTeam = async (formData: FormData) => {
  return await axios({
    method: "post",
    url: apiBaseUrl + "/api/request-teams",
    data: formData,
    headers,
  });
};

export const editTeam = async (formData: FormData) => {
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/edit-request-team",
    data: formData,
    headers,
  });
};

export const getAllJgTeams = async () => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/jg-teams",
    config
  );
  return data as JgTeam[];
};

export const createJgTeams = async (teamNames: string[]) => {
  const formData = new FormData();
  formData.append('names', JSON.stringify(teamNames));
  
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/jg-teams",
    data: formData,
    headers,
  });
};

export const getAllManualTeams = async () => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/teams-manual",
    config
  );
  return data as ManualTeam[];
};

export const createManualTeam = async (team: ManualTeam) => {
  const formData = new FormData();
  formData.append('name', team.name);
  formData.append('teamType', team.teamType);
  
  await axios({
    method: "post",
    url: apiBaseUrl + "/api/teams-manual",
    data: formData,
    headers,
  });
};

export type TeamsMemberQueueFilter = {
  status?: string;
  team_name?: string;
  user_email?: string;
  limit?: number;
};

export type TeamsMemberQueueResponse = {
  summary: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
    total: number;
  };
  teams: {
    team_id: string;
    team_name: string;
    total_members: number | string;
    completed: number | string;
    pending: number | string;
    failed: number | string;
    processing?: number;
  }[];
  details: {
    id: number;
    team_name: string;
    team_id: string;
    user_email: string;
    user_role: string;
    status: string;
    retry_count: number;
    error_message: string;
    created_at: string;
    updated_at: string;
  }[];
  filter: {
    status?: string;
  };
};

export const getTeamsMemberQueue = async (filter: TeamsMemberQueueFilter = {}) => {
  const params = new URLSearchParams();
  if (filter.status) params.append('status', filter.status);
  if (filter.team_name) params.append('team_name', filter.team_name);
  if (filter.user_email) params.append('user_email', filter.user_email);
  if (filter.limit) params.append('limit', filter.limit.toString());
  
  const queryString = params.toString();
  const url = apiBaseUrl + "/api/teams-member-queue" + (queryString ? "?" + queryString : "");
  
  const { data } = await axios.get(url, config);
  return data as TeamsMemberQueueResponse;
};

export const getTeamsMemberQueueByTeam = async (teamName: string) => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/teams-member-queue?team_name=" + encodeURIComponent(teamName),
    config
  );
  return data as TeamsMemberQueueResponse;
};

export type JgTeamStudent = {
  STUD_USERNAME: string;
  STUD_VORNAME: string;
  STUD_NACHNAME: string;
  STG: string;
  JAHR: string;
};

export type JgTeamStudentsResponse = {
  success: boolean;
  jgName: string;
  studentCount: number;
  students: JgTeamStudent[];
};

export const getJgTeamStudents = async (jgName: string) => {
  const { data } = await axios.get(
    apiBaseUrl + "/api/jg-teams/" + encodeURIComponent(jgName) + "/students",
    config
  );
  return data as JgTeamStudentsResponse;
};

export type SystemUser = {
  username: string;
  firstname: string;
  lastname: string;
  role: string;
  teamsRole: string;
  departments: string[];
  fachbereiche: string[];
};

export type UsersResponse = {
  data: SystemUser[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    total_pages: number;
  };
};

export const getAllSystemUsers = async (page: number = 1, perPage: number = 50) => {
  const { data } = await axios.get(
    apiBaseUrl + `/api/users?page=${page}&per_page=${perPage}`,
    config
  );
  return data as UsersResponse;
};

export type MoodleCacheCourse = {
  id: number;
  fullname: string;
  shortname: string;
  idnumber: string;
  categoryid: number;
  visible: number;
};

export type MoodleCacheResponse = {
  data: MoodleCacheCourse[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    total_pages: number;
  };
};

export type MoodleLiveStatusResult = {
  exists: boolean;
  id?: number;
  fullname?: string;
  teachers?: number;
  students?: number;
  error?: string;
};

export type MoodleLiveStatusResponse = {
  results: { [shortname: string]: MoodleLiveStatusResult };
};

export const getMoodleCacheCourses = async (page: number = 1, perPage: number = 50, search?: string) => {
  const params = new URLSearchParams();
  params.append('page', page.toString());
  params.append('per_page', perPage.toString());
  if (search) params.append('search', search);

  const { data } = await axios.get(
    apiBaseUrl + "/api/admin/moodle-cache?" + params.toString(),
    config
  );
  return data as MoodleCacheResponse;
};

export const checkMoodleLiveStatus = async (shortnames: string[]) => {
  const { data } = await axios.post(
    apiBaseUrl + "/api/admin/moodle-cache/check-live",
    { shortnames },
    config
  );
  return data as MoodleLiveStatusResponse;
};

export const deleteMoodleCacheEntry = async (id: number) => {
  const { data } = await axios.delete(
    apiBaseUrl + `/api/admin/moodle-cache/${id}`,
    config
  );
  return data;
};
